import { useTranslation } from 'react-i18next';
import { useForm } from 'modules/ui/form/useForm';
import {InputWithLabel, Loader, SelectWithLabel, Button, ButtonType} from 'modules/ui';
import { FormButtons, FormContainer } from 'modules/ui/form';
import { ValidationType } from 'types/ValidationType';
import {useAppDispatch, useAppSelector} from 'modules/hooks/appHooks';
import {
  parseFromCurrencyString,
  parseToCurrencyString,
} from 'modules/utils/numberFormatUtils';
import { useLeasingCalculatorCodes } from 'modules/hooks/codesHooks/useLeasingCalculatorCodes';
import { InputWithDisclaimer } from 'modules/ui/input';
import { SufixType } from 'modules/ui/input/common/SufixType';
import { LeasingCalculatorCodesSelectors } from 'modules/hooks/codesHooks/redux/leasingCalculatorCodes';
import { LeasingCalculatorSharedFormProps } from '../../common/LeasingCalculatorData';
import {
  calculatedFinancingAmountByLeasingType,
  getParticipationAmountLabel,
  getParticipationPropertyByLeasingType,
} from '../../common/helper';
import {useLeasingCalculatorObjectOptions} from "../../objectData/hooks/useLeasingCalculatorObjectOptions";
import {PDV} from "../../infoCalculation/components/common/helper";
import {setRemainingValue} from "../../../../hooks/codesHooks/redux/leasingCalculatorCodes/slice";
import {useEffect, useState} from "react";

export const LeasingCalculatorFinancialData: React.FC<
  LeasingCalculatorSharedFormProps
> = ({ defaultValues, onChange, onNextStep, onPreviousStep }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const financialDataCodesLoading = useAppSelector(
    LeasingCalculatorCodesSelectors.selectFinancingPeriodsLoading,
  );

  const remainingValues = useAppSelector(
      LeasingCalculatorCodesSelectors.selectRemainingValues,
  );

  const {
    fetchLeasingRemainingValue,
  } = useLeasingCalculatorObjectOptions(defaultValues);

  const { leasingTaxRates, paymentPeriodTypes, financingPeriods } =
    useLeasingCalculatorCodes();

  const { validateFlag, handleOnSubmit, updateFormData, updateSelectData } =
    useForm({
      onChange,
      onNextStep,
      onPreviousStep,
    });

  const [financingAmount, setFinancingAmount] = useState('');
  const [remainingValueFinancial, setRemainingValueFinancial] = useState('');

  useEffect(() => {
    if (
      defaultValues?.leasingType &&
      defaultValues?.leasingTaxRate &&
      defaultValues?.leasingNetObjectPrice &&
      defaultValues?.leasingObjectPpmv &&
      (defaultValues?.financialLeasingParticipation ||
        defaultValues?.operationalLeasingParticipation)
    ) {
      const newFinancingAmount = calculatedFinancingAmountByLeasingType(
        defaultValues.leasingNetObjectPrice,
        defaultValues.leasingObjectPpmv,
        defaultValues.leasingTaxRate.name,
        defaultValues.leasingType.name,
        defaultValues?.financialLeasingParticipation,
        defaultValues?.operationalLeasingParticipation,
      );

      setFinancingAmount((prevState) => {
        if (prevState === newFinancingAmount) {
          return prevState;
        }
        updateFormData('financingAmount', newFinancingAmount);
        return newFinancingAmount;
      });
    }
  }, [
    updateFormData,
    defaultValues?.leasingType,
    defaultValues?.leasingNetObjectPrice,
    defaultValues?.leasingObjectPpmv,
    defaultValues?.leasingTaxRate,
    defaultValues?.financialLeasingParticipation,
    defaultValues?.operationalLeasingParticipation,
  ]);

  const fetchRest = () => {
    fetchLeasingRemainingValue(defaultValues);
  }

  const updateParticipationAmount = (value: string) => {
    const propertyName = getParticipationPropertyByLeasingType(
      defaultValues?.leasingType?.name,
    );

    updateFormData(propertyName, parseFromCurrencyString(value));
  };

  const calculateRemainingValue = () => {
    if(remainingValues && defaultValues?.leasingType?.name === "Operativni"){
      return remainingValues.value;
    }
    else {
      const value = (Number(defaultValues?.leasingNetObjectPrice) * Number(PDV) + Number(defaultValues?.leasingObjectPpmv)) * (Number(parseFromCurrencyString(defaultValues?.leftoverPercentage?.replaceAll(',', ''))) / 10000);
      setRemainingValueFinancial(String(value ?? 0));
    }
  };

  useEffect(() => {
    if((Number(parseFromCurrencyString(defaultValues?.leftoverPercentage?.replaceAll(',', ''))) > 1000)){
      updateFormData('leftoverPercentage', "1000")
    }
    calculateRemainingValue()
  }, [defaultValues?.leftoverPercentage, defaultValues?.leasingNetObjectPrice, defaultValues?.leasingObjectPpmv]);

  const onRemainingValuePercentageChange = (value: string) => {
    const percentageAsNumber = Number(value.replace(",", "."))
    const v = Number(defaultValues?.leasingObjectPrice) * (percentageAsNumber / 100);
    dispatch(setRemainingValue({
      percentage: percentageAsNumber,
      value: v
    }))
  }

  useEffect(() => {
    updateFormData("remainingValuePercentage", remainingValues?.percentage)
  }, [remainingValues, remainingValues?.percentage, remainingValues?.value]);

  return (
    <Loader active={financialDataCodesLoading}>
      <FormContainer noValidate onSubmit={handleOnSubmit}>
        <InputWithDisclaimer
          required
          label={t('leasingCalculator.financialData.leasingObjectPrice')}
          placeholder={t(
            'leasingCalculator.financialData.leasingObjectPricePlaceholder',
          )}
          parser={(value) => value && parseToCurrencyString(value)}
          defaultValue={defaultValues?.leasingObjectPrice}
          onBlur={(e) =>
            updateFormData(
              'leasingObjectPrice',
              parseFromCurrencyString(e.target.value),
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
          disclaimer={t(
            'leasingCalculator.financialData.leasingObjectPriceInfoText',
          )}
          sufix={SufixType.Euro}
        />
        <InputWithLabel
          required
          label={t('leasingCalculator.financialData.leasingNetObjectPrice')}
          placeholder={t(
            'leasingCalculator.financialData.leasingNetObjectPricePlaceholder',
          )}
          parser={(value) => value && parseToCurrencyString(value)}
          defaultValue={defaultValues?.leasingNetObjectPrice}
          onBlur={(e) =>
            updateFormData(
              'leasingNetObjectPrice',
              parseFromCurrencyString(e.target.value),
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
          sufix={SufixType.Euro}
        />
        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.financialData.leasingTaxRate')}
          options={leasingTaxRates.options}
          defaultValue={leasingTaxRates.options[0]?.value}
          onChange={(value) =>
            updateSelectData('leasingTaxRate', value, leasingTaxRates.uwtTypes)
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
          readOnly
        />
        {defaultValues?.leasingObjectType?.name && defaultValues?.leasingObjectType?.name !== 'Nekretnine' &&
            <InputWithLabel
                required
                label={t('leasingCalculator.financialData.leasingObjectPpmv')}
                placeholder={t(
                    'leasingCalculator.financialData.leasingObjectPpmvPlaceholder',
                )}
                parser={(value) => value && parseToCurrencyString(value)}
                defaultValue={defaultValues?.leasingObjectPpmv}
                onBlur={(e) =>
                    updateFormData(
                        'leasingObjectPpmv',
                        parseFromCurrencyString(e.target.value),
                    )
                }
                validators={[ValidationType.REQUIRED]}
                validate={validateFlag}
                sufix={SufixType.Euro}
            />
        }
        <InputWithLabel
          required
          label={getParticipationAmountLabel(defaultValues?.leasingType?.name)}
          placeholder={t(
            'leasingCalculator.financialData.participationAmountPlaceholder',
          )}
          parser={(value) => value && parseToCurrencyString(value)}
          defaultValue={
            defaultValues?.[
              `${getParticipationPropertyByLeasingType(
                defaultValues?.leasingType?.name,
              )}` as keyof typeof defaultValues
            ] as number
          }
          onBlur={(e) => updateParticipationAmount(e.target.value)}
          validators={[ValidationType.REQUIRED, ValidationType.PARTICIPATION]}
          validate={validateFlag}
          sufix={SufixType.Percentage}
        />
        <InputWithLabel
          required
          label={t('leasingCalculator.financialData.financingAmount')}
          defaultValue={financingAmount}
          onBlur={(e) => updateFormData('financingAmount', e.target.value)}
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
          readOnly
          sufix={SufixType.Euro}
        />
        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.financialData.leasingPaymentPeriodType')}
          placeholderText={t(
            'leasingCalculator.financialData.leasingPaymentPeriodTypePlaceholder',
          )}
          options={paymentPeriodTypes.options}
          defaultValue={defaultValues?.leasingPaymentPeriodType?.id}
          onChange={(value) =>
            updateSelectData(
              'leasingPaymentPeriodType',
              value,
              paymentPeriodTypes.uwtTypes,
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />
        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.financialData.financingPeriod')}
          options={financingPeriods.options}
          placeholderText={t(
            'leasingCalculator.financialData.financingPeriodPlaceholder',
          )}
          defaultValue={defaultValues?.financingPeriod?.id}
          onChange={(value) =>
            updateSelectData(
              'financingPeriod',
              value,
              financingPeriods.uwtTypes,
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />
        {(defaultValues !== undefined && defaultValues?.leasingType?.name === 'Operativni') &&
            <Button
                text={t('form.formButtons.fetchRest')}
                type={ButtonType.Primary}
                onClick={fetchRest}
            />
        }
        {(defaultValues !== undefined && remainingValues !== undefined && defaultValues?.leasingType?.name === 'Operativni') &&
            <>
              <InputWithLabel
                  label={t('leasingCalculator.financialData.percentageRestValueOperational')}
                  placeholder={t(
                      'leasingCalculator.financialData.percentageRestValueOperational',
                  )}
                  labelTooltip={t(
                      'leasingCalculator.infoCalculation.operationalLeasing.futureValuePercentageTooltip',
                  )}
                  parser={(value) => value && parseToCurrencyString(value)}
                  defaultValue={remainingValues?.percentage}
                  sufix={SufixType.Percentage}
                  onBlur={(e) => onRemainingValuePercentageChange(e.target.value)}
              />
              <InputWithLabel
                  label={t('leasingCalculator.financialData.restValueOperational')}
                  placeholder={t(
                      'leasingCalculator.financialData.restValueOperational',
                  )}
                  parser={(value) => value && parseToCurrencyString(value)}
                  defaultValue={calculateRemainingValue()}
                  sufix={SufixType.Euro}
                  readOnly={true}
              />
            </>
        }
        {(defaultValues !== undefined && defaultValues?.leasingType?.name === 'Financijski') &&
            <>
              <InputWithLabel
                  label={t('leasingCalculator.financialData.percentageRestValueFinancial')}
                  placeholder={t(
                      'leasingCalculator.financialData.percentageRestValueFinancial',
                  )}
                  labelTooltip={t(
                      'leasingCalculator.infoCalculation.financialLeasing.futureValuePercentageTooltip',
                  )}
                  defaultValue={defaultValues.leftoverPercentage}
                  onChange={(e) => updateFormData('leftoverPercentage', e.target.value)}
                  value={parseToCurrencyString(defaultValues.leftoverPercentage ?? "")}
                  parser={(value) => value && parseToCurrencyString(value)}
                  sufix={SufixType.Percentage}
              />
              <InputWithLabel
                  label={t('leasingCalculator.financialData.restValueFinancial')}
                  placeholder={t(
                      'leasingCalculator.financialData.restValueFinancial',
                  )}
                  parser={(value) => value && parseToCurrencyString(value)}
                  defaultValue={remainingValueFinancial}
                  sufix={SufixType.Euro}
                  readOnly
              />
            </>
        }
        <FormButtons onPreviousStep={onPreviousStep} />
      </FormContainer>
    </Loader>
  );
};
